import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { History, Plus, ArrowRightLeft, Download, Upload, Wrench, Trash2, Calendar, MapPin, User as UserIcon, ShoppingBag, Package } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";

const ACTION_META = {
  create:          { label: "CREAT / PRIMIT", color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: Plus },
  move:            { label: "MUTAT",          color: "bg-blue-100 text-blue-800 border-blue-200",          Icon: ArrowRightLeft },
  pickup:          { label: "PRELUAT",        color: "bg-cyan-100 text-cyan-800 border-cyan-200",          Icon: Download },
  handover:        { label: "PREDAT",         color: "bg-purple-100 text-purple-800 border-purple-200",    Icon: Upload },
  split_assign:    { label: "PREDAT (split)", color: "bg-purple-100 text-purple-800 border-purple-200",    Icon: Upload },
  repair_logged:   { label: "REPARAȚIE",      color: "bg-orange-100 text-orange-800 border-orange-300",    Icon: Wrench },
  repair_deleted:  { label: "REPARAȚIE ANULATĂ", color: "bg-red-100 text-red-800 border-red-200",          Icon: Trash2 },
  scrapped:        { label: "CASAT",           color: "bg-red-200 text-red-900 border-red-400",            Icon: Trash2 },
  transfer_accepted: { label: "TRANSFER ACCEPTAT", color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: Download },
  transfer_refused:  { label: "TRANSFER REFUZAT",  color: "bg-red-100 text-red-800 border-red-200",        Icon: Trash2 },
  historical_assigned: { label: "ISTORIC · ALOCAT FOST ANGAJAT", color: "bg-zinc-200 text-zinc-800 border-zinc-400", Icon: UserIcon },
  historical_returned: { label: "ISTORIC · RETURNAT",            color: "bg-zinc-200 text-zinc-800 border-zinc-400", Icon: UserIcon },
  historical_lost:     { label: "ISTORIC · PIERDUT",             color: "bg-red-100 text-red-800 border-red-300",   Icon: Trash2 },
};

const locLabel = (loc, color, row, col) => {
  const parts = [];
  if (loc) parts.push(loc);
  if (color) parts.push(color);
  if (row && col) parts.push(`${row}${col}`);
  return parts.length ? parts.join(" · ") : "—";
};

// Sort cronologic ASC (cel mai vechi sus, fluxul curge în jos)
const tsKey = (h) => {
  const v = h.ts || h.created_at || "";
  return typeof v === "string" ? v : String(v);
};

export default function HistoryDialog({ item, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyMoves, setOnlyMoves] = useState(false);
  const [showHistForm, setShowHistForm] = useState(false);
  const [historical, setHistorical] = useState([]);
  const [histForm, setHistForm] = useState({ hid: "", action: "historical_assigned", from_date: "", to_date: "", note: "" });
  const [histSaving, setHistSaving] = useState(false);
  const [showCreateHist, setShowCreateHist] = useState(false);
  const [newHistNume, setNewHistNume] = useState("");
  const [newHistPrenume, setNewHistPrenume] = useState("");

  const loadRows = () => {
    setLoading(true);
    api.get(`/inventory/${item.id}/history`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRows();
    api.get(`/historical-persons`).then((r) => setHistorical(r.data || [])).catch(() => setHistorical([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const submitHist = async () => {
    if (!histForm.hid) { try { (await import("sonner")).toast.error("Selectează un fost angajat"); } catch { /* noop */ } return; }
    setHistSaving(true);
    try {
      await api.post(`/inventory/${item.id}/historical-assignment`, {
        historical_person_id: histForm.hid,
        action: histForm.action,
        from_date: histForm.from_date || null,
        to_date: histForm.to_date || null,
        note: histForm.note || null,
      });
      const sonner = await import("sonner");
      sonner.toast.success("Înregistrare istorică adăugată");
      setShowHistForm(false);
      setHistForm({ hid: "", action: "historical_assigned", from_date: "", to_date: "", note: "" });
      loadRows();
    } catch (e) {
      const sonner = await import("sonner");
      sonner.toast.error(e?.response?.data?.detail || "Eroare la adăugare");
    } finally { setHistSaving(false); }
  };

  const createHistorical = async () => {
    if (!newHistNume.trim()) {
      const sonner = await import("sonner");
      sonner.toast.error("Numele este obligatoriu");
      return;
    }
    try {
      const r = await api.post(`/historical-persons`, {
        nume: newHistNume.trim(),
        prenume: newHistPrenume.trim() || null,
      });
      const sonner = await import("sonner");
      sonner.toast.success("Fost angajat înregistrat");
      const hid = r.data.id;
      setHistorical((prev) => [...prev, r.data]);
      setHistForm((f) => ({ ...f, hid }));
      setNewHistNume(""); setNewHistPrenume("");
      setShowCreateHist(false);
    } catch (e) {
      const sonner = await import("sonner");
      sonner.toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  // ASC cronologic + filtrare opțională (doar preluări/predări/mutări)
  const orderedRows = useMemo(() => {
    let list = [...(rows || [])];
    if (onlyMoves) {
      const set = new Set(["pickup", "handover", "split_assign", "move", "create", "transfer_accepted", "transfer_refused"]);
      list = list.filter((h) => set.has(h.action));
    }
    list.sort((a, b) => {
      const ka = tsKey(a);
      const kb = tsKey(b);
      if (ka === kb) return 0;
      return ka < kb ? -1 : 1; // ASC
    });
    return list;
  }, [rows, onlyMoves]);

  const total = rows.length;
  const pickupCount = rows.filter((r) => r.action === "pickup").length;
  const handoverCount = rows.filter((r) => ["handover", "split_assign"].includes(r.action)).length;
  const repairCount = rows.filter((r) => r.action === "repair_logged").length;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} className="text-zinc-600"/>
            Istoric — {item.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Origine, apoi traseul echipamentului — în ordine cronologică (cel mai vechi sus, cel mai nou jos).
          </DialogDescription>
        </DialogHeader>

        {/* CARD ORIGINE — Producție / Cumpărare / Primire */}
        <div className="rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-emerald-100/40 p-3 mt-1" data-testid="history-origin">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-2 flex items-center gap-1">
            <Package size={12}/> Origine
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-white rounded p-2 border border-emerald-200">
              <div className="text-[10px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                <ShoppingBag size={10}/> Data cumpărării
              </div>
              <div className="font-display text-sm font-bold text-zinc-800 tabular-nums mt-0.5" data-testid="history-purchase-date">
                {item.purchase_date ? fmtRoDate(item.purchase_date) : <span className="text-zinc-400 italic font-normal">—</span>}
              </div>
            </div>
            <div className="bg-white rounded p-2 border border-emerald-200">
              <div className="text-[10px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                <Calendar size={10}/> Primit pe stoc
              </div>
              <div className="font-display text-sm font-bold text-zinc-800 tabular-nums mt-0.5" data-testid="history-received-date">
                {item.created_at ? fmtRoDate(item.created_at) : <span className="text-zinc-400 italic font-normal">—</span>}
              </div>
            </div>
            <div className="bg-white rounded p-2 border border-emerald-200">
              <div className="text-[10px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                <ShoppingBag size={10}/> Furnizor
              </div>
              <div className="text-sm font-bold text-zinc-800 mt-0.5 truncate" title={item.supplier || ""}>
                {item.supplier || <span className="text-zinc-400 italic font-normal">—</span>}
              </div>
            </div>
            <div className="bg-white rounded p-2 border border-emerald-200">
              <div className="text-[10px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                💰 Preț CHF
              </div>
              <div className="font-display text-sm font-bold text-zinc-800 tabular-nums mt-0.5">
                {item.price_chf != null && item.price_chf !== "" ? Number(item.price_chf).toFixed(2) : <span className="text-zinc-400 italic font-normal">—</span>}
              </div>
            </div>
          </div>
          {(item.serial || item.no_serial) && (
            <div className="mt-2 text-[11px] text-emerald-900">
              {item.serial ? <>Nr. serie: <span className="font-mono font-bold">{item.serial}</span></> : <span className="italic">fără număr de serie</span>}
            </div>
          )}
        </div>

        {/* Sumar KPI + filtru */}
        <div className="flex items-center gap-3 flex-wrap py-2 border-b mt-2" data-testid="history-summary">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Total <strong className="text-zinc-800 tabular-nums">{total}</strong></span>
            <span className="text-[10px] uppercase tracking-wider text-cyan-700 font-bold bg-cyan-50 px-1.5 py-0.5 rounded">Preluări <strong className="tabular-nums">{pickupCount}</strong></span>
            <span className="text-[10px] uppercase tracking-wider text-purple-700 font-bold bg-purple-50 px-1.5 py-0.5 rounded">Predări <strong className="tabular-nums">{handoverCount}</strong></span>
            <span className="text-[10px] uppercase tracking-wider text-orange-700 font-bold bg-orange-50 px-1.5 py-0.5 rounded">Reparații <strong className="tabular-nums">{repairCount}</strong></span>
          </div>
          <label className="ml-auto text-[11px] inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="history-filter-toggle">
            <input
              type="checkbox"
              checked={onlyMoves}
              onChange={(e) => setOnlyMoves(e.target.checked)}
              className="rounded"
            />
            <span className="text-zinc-700 font-semibold">Doar traseul (fără reparații)</span>
          </label>
          <button
            type="button"
            onClick={() => setShowHistForm((v) => !v)}
            className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded border-2 border-zinc-400 bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            data-testid="add-historical-inv-toggle"
            title="Înregistrează retroactiv că un fost angajat a folosit acest echipament"
          >
            🕘 + Istoric fost angajat
          </button>
        </div>

        {showHistForm && (
          <div className="card-flat p-3 mt-2 bg-zinc-50 border-l-4 border-l-zinc-500 space-y-2" data-testid="historical-inv-form">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600">Alocare istorică (fost angajat)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500">Fost angajat</label>
                <select
                  className="mt-1 w-full text-sm rounded border border-zinc-300 px-2 py-1.5"
                  value={histForm.hid}
                  onChange={(e) => setHistForm({ ...histForm, hid: e.target.value })}
                  data-testid="hist-inv-hid"
                >
                  <option value="">— selectează —</option>
                  {historical.map((h) => <option key={h.id} value={h.id}>{h.nume} {h.prenume || ""}</option>)}
                </select>
                {!showCreateHist ? (
                  <button
                    type="button"
                    onClick={() => setShowCreateHist(true)}
                    className="text-[11px] text-blue-700 hover:underline mt-1"
                    data-testid="hist-inv-create-toggle"
                  >
                    ➕ Adaugă fost angajat nou
                  </button>
                ) : (
                  <div className="mt-1 p-2 bg-white border border-zinc-200 rounded">
                    <div className="grid grid-cols-2 gap-1.5">
                      <input
                        placeholder="Nume"
                        value={newHistNume}
                        onChange={(e) => setNewHistNume(e.target.value)}
                        className="text-sm rounded border border-zinc-300 px-2 py-1"
                        data-testid="hist-inv-new-nume"
                      />
                      <input
                        placeholder="Prenume (opțional)"
                        value={newHistPrenume}
                        onChange={(e) => setNewHistPrenume(e.target.value)}
                        className="text-sm rounded border border-zinc-300 px-2 py-1"
                        data-testid="hist-inv-new-prenume"
                      />
                    </div>
                    <div className="flex gap-1.5 mt-1.5">
                      <button onClick={createHistorical} className="text-[11px] font-bold px-2 py-1 bg-zinc-800 text-white rounded" data-testid="hist-inv-new-save">Salvează</button>
                      <button onClick={() => { setShowCreateHist(false); setNewHistNume(""); setNewHistPrenume(""); }} className="text-[11px] px-2 py-1 bg-white border border-zinc-300 rounded">Anulează</button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500">Tip acțiune</label>
                <select
                  className="mt-1 w-full text-sm rounded border border-zinc-300 px-2 py-1.5"
                  value={histForm.action}
                  onChange={(e) => setHistForm({ ...histForm, action: e.target.value })}
                  data-testid="hist-inv-action"
                >
                  <option value="historical_assigned">Alocat (a primit echipamentul)</option>
                  <option value="historical_returned">Returnat (a predat înapoi)</option>
                  <option value="historical_lost">Pierdut (a pierdut echipamentul)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500">De pe data (opțional)</label>
                <input
                  type="date"
                  value={histForm.from_date}
                  onChange={(e) => setHistForm({ ...histForm, from_date: e.target.value })}
                  className="mt-1 w-full text-sm rounded border border-zinc-300 px-2 py-1.5"
                  data-testid="hist-inv-from"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-zinc-500">Până pe data (opțional)</label>
                <input
                  type="date"
                  value={histForm.to_date}
                  onChange={(e) => setHistForm({ ...histForm, to_date: e.target.value })}
                  className="mt-1 w-full text-sm rounded border border-zinc-300 px-2 py-1.5"
                  data-testid="hist-inv-to"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase font-bold text-zinc-500">Notă (opțional)</label>
                <textarea
                  rows={2}
                  value={histForm.note}
                  onChange={(e) => setHistForm({ ...histForm, note: e.target.value })}
                  className="mt-1 w-full text-sm rounded border border-zinc-300 px-2 py-1.5"
                  placeholder="ex: a folosit echipamentul pe șantierul X în perioada Y"
                  data-testid="hist-inv-note"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowHistForm(false); }}
                className="text-[11px] px-3 py-1.5 bg-white border border-zinc-300 rounded"
                disabled={histSaving}
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={submitHist}
                className="text-[11px] font-bold px-3 py-1.5 bg-zinc-800 text-white rounded hover:bg-zinc-900 disabled:opacity-50"
                disabled={histSaving}
                data-testid="hist-inv-save"
              >
                {histSaving ? "Se adaugă…" : "Adaugă înregistrare istorică"}
              </button>
            </div>
          </div>
        )}

        {/* TIMELINE — ordine cronologică ASC */}
        <div className="flex-1 overflow-y-auto pr-1 mt-3" data-testid="history-list">
          {loading ? (
            <div className="text-sm text-zinc-500 text-center py-6 italic">Se încarcă...</div>
          ) : orderedRows.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-12 italic" data-testid="history-empty">
              Fără mișcări pe acest echipament.
            </div>
          ) : (
            <ol className="relative border-l-2 border-zinc-200 ml-2 space-y-3 py-1">
              {orderedRows.map((h) => {
                const meta = ACTION_META[h.action] || { label: (h.action || "").toUpperCase(), color: "bg-zinc-100 text-zinc-700 border-zinc-200", Icon: History };
                const Icon = meta.Icon;
                const dateLabel = fmtRoDate(h.ts) || "—";
                return (
                  <li key={h.id} className="ml-4 relative" data-testid={`history-row-${h.id}`}>
                    {/* Bullet point pe timeline */}
                    <span className="absolute -left-[1.45rem] top-1.5 w-3 h-3 rounded-full bg-white border-2 border-zinc-400"/>
                    <div className="rounded-lg border bg-white p-3 hover:shadow-sm transition-shadow">
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}>
                          <Icon size={11}/> {meta.label}
                        </div>
                        <div className="inline-flex items-center gap-1 text-[11px] font-bold tabular-nums bg-zinc-100 text-zinc-800 px-1.5 py-0.5 rounded">
                          <Calendar size={10}/> {dateLabel}
                        </div>
                        <div className="ml-auto text-[11px] font-semibold text-zinc-600 truncate max-w-[180px]">
                          {h.user || h.actor_name || "—"}
                        </div>
                      </div>

                      {/* Detalii per acțiune */}
                      <div className="mt-2 text-sm leading-snug">
                        {h.action === "create" && (
                          <div className="text-zinc-700">
                            Primit pe stoc la <strong>{locLabel(h.to_location || h.location, h.to_color || h.color, h.to_shelf_row || h.shelf_row, h.to_shelf_col || h.shelf_col)}</strong>
                            {h.to_assignee && <> · 👤 asignat <strong>{h.to_assignee}</strong></>}
                          </div>
                        )}

                        {h.action === "move" && (
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <MapPin size={12} className="text-zinc-400"/>
                              <span className="text-zinc-500">{locLabel(h.from_location, h.from_color, h.from_shelf_row, h.from_shelf_col)}</span>
                              <span className="text-zinc-400">→</span>
                              <span className="font-semibold text-zinc-800">{locLabel(h.to_location, h.to_color, h.to_shelf_row, h.to_shelf_col)}</span>
                            </div>
                            {(h.from_assignee || h.to_assignee) && (
                              <div className="flex items-center gap-2 flex-wrap mt-1">
                                <UserIcon size={12} className="text-zinc-400"/>
                                <span className="text-zinc-500">{h.from_assignee || <span className="italic">(magazie)</span>}</span>
                                <span className="text-zinc-400">→</span>
                                <span className="font-semibold text-emerald-700">{h.to_assignee || <span className="italic">(magazie)</span>}</span>
                              </div>
                            )}
                            {h.to_car_label && (
                              <div className="text-[11px] text-blue-700 mt-1">🚗 {h.to_car_label}</div>
                            )}
                          </div>
                        )}

                        {(h.action === "pickup" || h.action === "handover") && (
                          <div className="text-zinc-700">{h.message || (h.action === "pickup" ? "Preluat" : "Predat")}</div>
                        )}

                        {h.action === "split_assign" && (
                          <div className="flex items-center gap-2 flex-wrap text-zinc-700">
                            <UserIcon size={12} className="text-purple-500"/>
                            <span>Predat <strong className="tabular-nums">{h.quantity ?? "—"}</strong> buc. către <strong className="text-purple-800">{h.to_assignee || "—"}</strong></span>
                            {h.remaining_stock != null && (
                              <span className="text-[10px] text-zinc-500">· rest stoc: <strong className="tabular-nums">{h.remaining_stock}</strong></span>
                            )}
                          </div>
                        )}

                        {h.action === "transfer_accepted" && (
                          <div className="text-emerald-700">✓ Transferul a fost acceptat de destinatar.</div>
                        )}
                        {h.action === "transfer_refused" && (
                          <div className="text-red-700">✗ Transferul a fost refuzat. Item revine la sursa.</div>
                        )}

                        {h.action === "repair_logged" && (
                          <div>
                            <div className="text-zinc-800">{h.message || "Reparație înregistrată"}</div>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                              {h.cost_chf != null && Number(h.cost_chf) > 0 && (
                                <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded tabular-nums">
                                  {Number(h.cost_chf).toFixed(2)} CHF
                                </span>
                              )}
                              {h.performed_by && (
                                <span className="text-[11px] text-orange-700">🔧 {h.performed_by}</span>
                              )}
                            </div>
                          </div>
                        )}

                        {h.action === "repair_deleted" && (
                          <div className="text-zinc-600 italic">Reparație ștearsă din istoric</div>
                        )}

                        {(h.action === "historical_assigned" || h.action === "historical_returned" || h.action === "historical_lost") && (
                          <div>
                            <div className="flex items-center gap-2 flex-wrap text-zinc-700">
                              <UserIcon size={12} className="text-zinc-500"/>
                              <span>
                                {h.action === "historical_assigned" && <>Alocat <strong>{h.counterparty_name}</strong></>}
                                {h.action === "historical_returned" && <>Returnat de <strong>{h.counterparty_name}</strong></>}
                                {h.action === "historical_lost" && <>Pierdut la <strong>{h.counterparty_name}</strong></>}
                                <span className="ml-1 text-[10px] uppercase font-bold text-zinc-500 bg-zinc-100 px-1 rounded">fost angajat</span>
                              </span>
                            </div>
                            {(h.from_date || h.to_date) && (
                              <div className="text-[11px] text-zinc-500 mt-0.5">
                                {h.from_date && <>de pe <strong>{fmtRoDate(h.from_date)}</strong></>}
                                {h.to_date && <> · până pe <strong>{fmtRoDate(h.to_date)}</strong></>}
                              </div>
                            )}
                          </div>
                        )}

                        {(h.note || h.recipient_note || h.reason) && (
                          <div className="text-[11px] text-zinc-500 mt-1 italic break-words">
                            💬 {h.note || h.recipient_note || h.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
